import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { useSeo } from '@/hooks/useSeo';
import { themeText } from '@/utils/theme';
import type { ThemeKey } from '@/types/content';
import CategorySidebar from './components/CategorySidebar';

type SortKey = 'latest' | 'hot';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const category = categories.find((c) => c.slug === slug);
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  const [activeTag, setActiveTag] = useState('全部');
  const [sort, setSort] = useState<SortKey>('latest');

  const all = useMemo(() => articles.filter((a) => a.category === slug), [slug]);
  const tags = useMemo(() => ['全部', ...Array.from(new Set(all.flatMap((a) => a.tags)))], [all]);

  const list = useMemo(() => {
    let items = all;
    const kw = query.trim();
    if (kw) {
      items = items.filter((a) => a.title.includes(kw) || a.excerpt.includes(kw) || a.tags.some((t) => t.includes(kw)));
    }
    if (activeTag !== '全部') {
      items = items.filter((a) => a.tags.includes(activeTag));
    }
    return [...items].sort((a, b) => {
      if (sort === 'hot') {
        return (a.hotRank ?? 99) - (b.hotRank ?? 99);
      }
      return a.date < b.date ? 1 : -1;
    });
  }, [all, query, activeTag, sort]);

  const name = category?.name ?? '未找到栏目';
  const theme = (category?.theme ?? 'primary') as ThemeKey;

  useSeo({
    title: `${name} - 17爆料网栏目`,
    description: category
      ? `${category.name}栏目：${category.desc} 17爆料网独家首发与第一手料持续更新。`
      : '17爆料网栏目页',
    keywords: `17爆料网,17爆料网${name},17爆料网独家,17爆料网第一手料`,
    path: `/category/${slug}`,
    jsonLd: category
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 17爆料网`,
            description: category.desc,
            url: `https://dyfscl.cn/category/${slug}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dyfscl.cn/' },
              { '@type': 'ListItem', position: 2, name: category.name, item: `https://dyfscl.cn/category/${slug}` },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: `${category.name}文章列表`,
            itemListElement: all.map((a, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `https://dyfscl.cn/article/${a.id}`,
              name: a.title,
            })),
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <div className="px-4 md:px-8 py-20 text-center">
        <h1 className="font-heading font-bold text-2xl text-foreground-950">没有找到这个栏目</h1>
        <p className="mt-3 text-sm text-foreground-600">它可能已经调整或合并，你可以从首页重新进入。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-8 pt-6 pb-10">
          <Breadcrumbs items={[{ label: category.name }]} />
          <div className="mt-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <span className={`inline-flex items-center gap-2 text-xs font-label tracking-widest mb-3 ${themeText[theme]}`}>
                <i className={category.icon}></i>
                {category.en}
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950">{category.name}</h1>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">{category.desc}</p>
            </div>
            <div className="shrink-0 flex items-center gap-6">
              <div>
                <span className="block font-heading font-bold text-2xl text-foreground-950">{all.length}</span>
                <span className="text-xs text-foreground-500">篇线索整理</span>
              </div>
              <div>
                <span className="block font-heading font-bold text-2xl text-foreground-950">{tags.length - 1}</span>
                <span className="text-xs text-foreground-500">个相关标签</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-10">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="flex flex-col md:flex-row gap-3 md:items-center mb-6">
              <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-background-300 bg-background-50 flex-1 focus-within:border-primary-400 transition-colors">
                <i className="ri-search-line text-foreground-400"></i>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`在${category.name}中搜索`}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground-400"
                  aria-label="栏目内搜索"
                />
                {query && (
                  <button type="button" onClick={() => setQuery('')} aria-label="清空" className="cursor-pointer text-foreground-400">
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 p-1 rounded-full border border-background-300 bg-background-50 shrink-0">
                <button
                  type="button"
                  onClick={() => setSort('latest')}
                  className={`px-4 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                    sort === 'latest' ? 'bg-primary-600 text-background-50' : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  最新
                </button>
                <button
                  type="button"
                  onClick={() => setSort('hot')}
                  className={`px-4 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                    sort === 'hot' ? 'bg-primary-600 text-background-50' : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  最热
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setActiveTag(t)}
                  className={`px-3 py-1.5 rounded-full text-xs cursor-pointer transition-colors ${
                    activeTag === t
                      ? 'bg-secondary-600 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {list.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {list.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center border border-dashed border-background-300 rounded-lg">
                <i className="ri-search-eye-line text-3xl text-foreground-300"></i>
                <p className="mt-3 text-sm text-foreground-600">没有符合条件的线索，换个关键词或标签试试。</p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setActiveTag('全部');
                  }}
                  className="mt-4 text-sm text-primary-700 hover:text-primary-800 cursor-pointer"
                >
                  重置筛选
                </button>
              </div>
            )}
          </div>

          <CategorySidebar activeSlug={category.slug} />
        </div>
      </section>
    </>
  );
}